import type { CloudRole } from '../types'

// The signed-in pages. The sidebar lists them, the top bar titles each page
// with its label, and sign-in refuses a return address the role cannot open.
export interface NavItem {
  path: string
  label: string
  icon: string
  ownerOnly?: boolean
  tenantOnly?: boolean
  cloudAdminOnly?: boolean
  // Tenant administrators and Cloud Admins.
  adminOnly?: boolean
}

export const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: 'dashboard', tenantOnly: true },
  { path: '/cores', label: 'Cores', icon: 'dns', tenantOnly: true },
  { path: '/sites', label: 'Sites', icon: 'location_city', tenantOnly: true },
  { path: '/events', label: 'Events', icon: 'timeline', tenantOnly: true },
  { path: '/users', label: 'Team', icon: 'people', ownerOnly: true, tenantOnly: true },
  { path: '/settings', label: 'Settings', icon: 'settings', tenantOnly: true },
  { path: '/tenants', label: 'Tenants', icon: 'admin_panel_settings', cloudAdminOnly: true },
  { path: '/audit', label: 'Audit', icon: 'fact_check', adminOnly: true },
]

export function canUseNavItem(item: NavItem, role: CloudRole | undefined): boolean {
  const isCloudAdmin = role === 'cloud_admin'
  const isTenantAdmin = role === 'owner' || role === 'tenant_admin'
  if (item.cloudAdminOnly && !isCloudAdmin) return false
  if (item.tenantOnly && isCloudAdmin) return false
  if (item.ownerOnly && !isTenantAdmin) return false
  if (item.adminOnly && !isTenantAdmin && !isCloudAdmin) return false
  return true
}

// homePath is the page a role starts on: after sign-in, at "/", and when it
// opens a page meant for the other kind of account.
export function homePath(role: CloudRole | undefined): string {
  return role === 'cloud_admin' ? '/tenants' : '/dashboard'
}

function navItemFor(pathname: string): NavItem | undefined {
  return navItems.find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
}

// landingPath is where sign-in sends a role. A requested return address is
// honoured only when it stays on this site and names a page the role can use.
export function landingPath(role: CloudRole | undefined, requested: string | null): string {
  const home = homePath(role)
  if (!requested?.startsWith('/')) return home
  let url: URL
  try {
    url = new URL(requested, window.location.origin)
  } catch {
    return home
  }
  if (url.origin !== window.location.origin) return home
  if (!canOpenPath(url.pathname, role)) return home
  return url.pathname + url.search + url.hash
}

// canOpenPath is the route guard: a page belonging to a sidebar entry (the
// entry itself or a page under it, such as /cores/:id) opens only for a role
// that may use that entry. The router and the sidebar share this one rule.
export function canOpenPath(pathname: string, role: CloudRole | undefined): boolean {
  const item = navItemFor(pathname)
  return item === undefined || canUseNavItem(item, role)
}

// pageTitle names the page at pathname for the top bar.
export function pageTitle(pathname: string): string {
  if (pathname.startsWith('/cores/')) return 'Core Details'
  return navItemFor(pathname)?.label ?? 'Sentyr Cloud'
}
